import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import SelectField from "components/admin/SelectField";
import { useNotify } from "components/admin/Feedback";
import { useFetch } from "components/admin/hooks";
import TaskEditDialog from "layouts/tasks/TaskEditDialog";
import PlayerProgress from "layouts/tasks/PlayerProgress";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";

const TYPE_COLORS = { 0: "info", 1: "warning", 2: "success", 3: "primary" };

function TaskList({ meta, tasks, onSaved }) {
  const notify = useNotify();
  const [type, setType] = useState("");
  const [npc, setNpc] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [editing, setEditing] = useState(null);
  const canEdit = useCan(PERMISSIONS.TASK_EDIT);

  const list = useFetch(
    () =>
      api.get("/api/admin/tasks", {
        type: type === "" ? undefined : type,
        npc: npc === "" ? undefined : npc,
        q,
        page,
        size,
      }),
    [type, npc, q, page, size]
  );

  const columns = [
    {
      key: "name",
      label: "Nhiệm vụ",
      wrap: true,
      render: (row) => (
        <MDBox lineHeight={1.2}>
          <MDTypography variant="button" fontWeight="medium" display="block">
            {row.name}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            #{row.taskId}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      key: "typeName",
      label: "Loại",
      width: 150,
      render: (row) => (
        <Chip size="small" color={TYPE_COLORS[row.type] || "default"} label={row.typeName} />
      ),
    },
    {
      key: "npcName",
      label: "NPC giao",
      wrap: true,
      render: (row) =>
        row.npcName ? (
          <Tooltip title={`Mã NPC ${row.fromNPC}`}>
            <span>{row.npcName}</span>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      key: "taskNeed",
      label: "Cần xong trước",
      wrap: true,
      render: (row) =>
        row.taskNeed?.length
          ? row.taskNeed.map((id) => tasks[id]?.name || `#${id}`).join(", ")
          : "—",
    },
    {
      key: "counts",
      label: "Yêu cầu / Quà",
      align: "center",
      render: (row) => (
        <MDBox display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
          <Tooltip
            title={
              row.requestCount === 0
                ? "Không có yêu cầu: người chơi nhận là xong ngay"
                : "Số việc người chơi phải làm"
            }
          >
            <Chip
              size="small"
              color={row.requestCount === 0 ? "error" : "default"}
              variant={row.requestCount === 0 ? "filled" : "outlined"}
              label={row.requestCount === 0 ? "Không yêu cầu" : `${row.requestCount} yêu cầu`}
            />
          </Tooltip>
          <Tooltip title={row.giftCount === 0 ? "Xong nhiệm vụ không được gì" : "Số dòng quà"}>
            <Chip
              size="small"
              color={row.giftCount === 0 ? "warning" : "default"}
              variant={row.giftCount === 0 ? "filled" : "outlined"}
              label={row.giftCount === 0 ? "Không quà" : `${row.giftCount} quà`}
            />
          </Tooltip>
        </MDBox>
      ),
    },
    canEdit && {
      key: "actions",
      label: "",
      align: "right",
      render: (row) => (
        <MDButton
          size="small"
          variant="outlined"
          color="info"
          onClick={() => setEditing(row.taskId)}
        >
          Sửa
        </MDButton>
      ),
    },
  ].filter(Boolean);

  return (
    <MDBox>
      <PageCard title="Tìm nhiệm vụ" color="info">
        <MDBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <SelectField
            size="small"
            label="Loại"
            value={type}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 190 }}
            onChange={(e) => {
              setType(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">Tất cả loại</MenuItem>
            {(meta?.types || []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </SelectField>
          <SelectField
            size="small"
            label="NPC giao"
            value={npc}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 220 }}
            onChange={(e) => {
              setNpc(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">Tất cả NPC</MenuItem>
            {(meta?.npcs || []).map((n) => (
              <MenuItem key={n.id} value={n.id}>
                {n.name}
              </MenuItem>
            ))}
          </SelectField>
          <MDInput
            size="small"
            label="Tìm theo tên hoặc mã"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
          <MDButton size="small" variant="outlined" color="info" onClick={() => list.reload()}>
            <Icon>refresh</Icon>&nbsp;Tải lại
          </MDButton>
        </MDBox>
        {list.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {list.error}
          </Alert>
        )}
      </PageCard>

      <MDBox mt={3}>
        <PageCard
          title={`${list.data?.total ?? 0} nhiệm vụ`}
          subtitle="Đỏ: nhận là xong ngay · Cam: xong không được quà"
          color="dark"
          noPadding
        >
          <AdminTable
            columns={columns}
            rows={list.data?.items || []}
            rowKey="taskId"
            loading={list.loading}
            page={page}
            size={size}
            total={list.data?.total}
            onPageChange={setPage}
            onSizeChange={(s) => {
              setSize(s);
              setPage(1);
            }}
            emptyText="Không có nhiệm vụ nào khớp bộ lọc"
          />
        </PageCard>
      </MDBox>

      {editing != null && (
        <TaskEditDialog
          taskId={editing}
          meta={meta}
          tasks={tasks}
          onClose={() => setEditing(null)}
          onSaved={(warning) => {
            if (warning) notify.error(warning);
            list.reload();
            onSaved();
          }}
        />
      )}
    </MDBox>
  );
}

TaskList.propTypes = {
  meta: PropTypes.object,
  tasks: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
};

// Tab ghi lên URL (?tab=) để gửi link thẳng tới phần tra người chơi.
const TABS = [
  { key: "templates", label: "Danh sách nhiệm vụ" },
  { key: "player", label: "Tra người chơi" },
];
const TAB_SX = { flex: { xs: "0 0 auto", md: "1 1 auto" }, px: 1.5, whiteSpace: "nowrap" };

function Tasks() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "templates";
  const pickTab = (v) => setParams(v === "templates" ? {} : { tab: v }, { replace: true });
  const meta = useFetch(() => api.get("/api/admin/tasks/meta"), []);
  // Có khoảng trăm nhiệm vụ: tải hết một lần để hiện tên ở mọi chỗ đang chỉ có mã.
  const all = useFetch(() => api.get("/api/admin/tasks", { size: 200 }), []);

  const tasks = useMemo(() => {
    const map = {};
    (all.data?.items || []).forEach((t) => {
      map[t.taskId] = t;
    });
    return map;
  }, [all.data]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="tasks" />
        {meta.error && <Alert severity="error">{meta.error}</Alert>}
        <MDBox mb={3}>
          <Tabs
            value={tab}
            onChange={(e, v) => pickTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ maxWidth: 480 }}
          >
            {TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={t.label} sx={TAB_SX} />
            ))}
          </Tabs>
        </MDBox>
        {tab === "templates" ? (
          <TaskList meta={meta.data} tasks={tasks} onSaved={all.reload} />
        ) : (
          <PlayerProgress tasks={tasks} />
        )}
        <MDBox mt={3}>
          <MDTypography variant="caption" color="text">
            Mọi thao tác ở trang này được ghi vào Lịch sử kèm tên admin và dữ liệu trước / sau.
          </MDTypography>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tasks;
